// Copyright 2026 Rockyljewell
// SPDX-License-Identifier: Apache-2.0

import { Camera } from "./camera.js";
import { AdaptiveCapturePolicy, resolveAdaptiveCaptureOptions, type AdaptiveCaptureOptions, type AdaptiveCapturePlan, type AdaptiveCaptureStats, type ResolvedAdaptiveCaptureOptions } from "./capture-policy.js";
import { DuplicateFilter } from "./duplicate-filter.js";
import { toBarcodes } from "./engine/convert.js";
import { createFrameDecoder, type FrameDecoder } from "./engine/decoder.js";
import type { DecodeParams } from "./engine/protocol.js";
import { QRGenError, toQRGenError } from "./errors.js";
import { Feedback } from "./feedback.js";
import { ALL_SYMBOLOGIES, resolveSymbologies, symbologyInfo, toZXingReadFormats, type Symbology } from "./symbologies.js";
import { BarcodeTracker, type TrackerUpdate } from "./tracker.js";
import type { Barcode, ScanArea, ScannerOptions, Size, ViewfinderStyle } from "./types.js";
import { Emitter } from "./util.js";

export type ScannerState = "idle" | "starting" | "scanning" | "paused" | "stopped" | "error";

export interface FrameEvent {
  barcodes: Barcode[];
  frameSize: Size;
  decodeMs: number;
}

export interface ScannerEvents extends Record<string, unknown> {
  /** New barcodes that passed the duplicate filter. */
  scan: { barcodes: Barcode[] };
  /** Batch mode: the full set of tracked barcodes after each frame. */
  track: TrackerUpdate;
  /** Every decoded frame (including duplicates), for custom overlays. */
  frame: FrameEvent;
  /** Intrinsic camera dimensions changed: discard overlays in the previous frame coordinates. */
  spatialreset: { frameSize: Size };
  error: QRGenError;
  state: ScannerState;
  ready: { engine: "worker" | "main"; camera: Camera };
}

export interface BarcodeScannerOptions extends ScannerOptions {
  /** Video element to render the camera into. A hidden one is created when omitted. */
  video?: HTMLVideoElement;
  /** Longest side, in pixels, of the image handed to the decoder. Default 1280. */
  maxDecodeSize?: number;
  /** Upper bound for decodes per second. Default 24. */
  maxFps?: number;
  /**
   * Also look for white-on-black codes. Default: alternate frames. On those frames,
   * inverted linear and PDF417 codes need a second decode, which runs only when the
   * frame's normal decode found nothing.
   */
  tryInvert?: boolean | "alternate";
  /** Experimental bounded full-frame capture adaptation. Default false (fixed capture). */
  adaptive?: boolean | AdaptiveCaptureOptions;
}

interface ResolvedOptions {
  symbologies: Symbology[];
  mode: "single" | "continuous" | "batch";
  duplicateFilter: number;
  beep: boolean;
  vibrate: boolean;
  scanArea: ScanArea | null;
  maxResults: number;
  tryHarder: boolean;
  maxDecodeSize: number;
  maxFps: number;
  tryInvert: boolean | "alternate";
  adaptive: ResolvedAdaptiveCaptureOptions | null;
  viewfinder: ViewfinderStyle;
}

function resolveOptions(o: BarcodeScannerOptions): ResolvedOptions {
  const symbologies = resolveSymbologies(o.symbologies);
  const mode = o.mode ?? "continuous";
  const onlyLinear = symbologies.length > 0 && symbologies.every((s) => symbologyInfo(s).kind === "linear");
  return {
    symbologies,
    mode,
    duplicateFilter: o.duplicateFilter ?? (mode === "batch" ? -1 : 1000),
    beep: o.beep ?? true,
    vibrate: o.vibrate ?? true,
    scanArea: o.scanArea ?? null,
    maxResults: o.maxResults ?? (mode === "batch" ? 20 : 1),
    // Batch codes can be anywhere in the frame; linear codes off-center need tryHarder.
    tryHarder: o.tryHarder ?? mode === "batch",
    maxDecodeSize: o.maxDecodeSize ?? 1280,
    maxFps: o.maxFps ?? 24,
    tryInvert: o.tryInvert ?? "alternate",
    adaptive: resolveAdaptiveCaptureOptions(o.adaptive, o.maxDecodeSize ?? 1280),
    viewfinder: o.viewfinder ?? (onlyLinear ? "line" : "frame"),
  };
}

/**
 * Camera barcode scanner: opens the camera, decodes frames off the main thread and
 * reports results with duplicate filtering, feedback and batch tracking.
 *
 * ```ts
 * const scanner = new BarcodeScanner({ symbologies: ["qr", "ean13"], video: myVideo });
 * scanner.on("scan", ({ barcodes }) => console.log(barcodes[0].data));
 * await scanner.start();
 * ```
 *
 * For a ready-made UI use the `<qrgen-scanner>` element from `qrgen-sdk/elements`.
 */
export class BarcodeScanner extends Emitter<ScannerEvents> {
  readonly camera: Camera;
  readonly video: HTMLVideoElement;
  readonly feedback: Feedback;
  readonly tracker = new BarcodeTracker();
  private opts: ResolvedOptions;
  private filter: DuplicateFilter;
  private pendingBatchScans = new Set<number>();
  private decoder: FrameDecoder | null = null;
  private capturePolicy: AdaptiveCapturePolicy | null = null;
  private _state: ScannerState = "idle";
  private busy = false;
  private destroying = false;
  private frameHandle = 0;
  private lastDecodeStart = 0;
  private frameCount = 0;
  private canvas: HTMLCanvasElement | OffscreenCanvas | null = null;
  private ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null = null;
  private ownsVideo: boolean;
  private lastBatchBeep = 0;
  private startToken = 0;
  private frameToken = 0;
  private switchToken = 0;
  private pendingCameraSwitch: { token: number; promise: Promise<void> } | null = null;
  private frameSize: Size | null = null;
  private readonly handleVideoResize = () => this.syncFrameSize();
  /** Rolling statistics for diagnostics. */
  readonly stats = { decodeMs: 0, fps: 0, engine: "" as "" | "worker" | "main", adaptive: null as AdaptiveCaptureStats | null };
  private fpsWindow: number[] = [];

  /** Options as the caller gave them; defaults are re-derived from these on every change. */
  private raw: BarcodeScannerOptions;

  constructor(options: BarcodeScannerOptions = {}) {
    super();
    const { video: _video, ...raw } = options;
    this.raw = raw;
    this.opts = resolveOptions(options);
    this.resetCapturePolicy();
    this.filter = new DuplicateFilter(this.opts.duplicateFilter);
    this.camera = new Camera({ camera: options.camera, resolution: options.resolution });
    this.feedback = new Feedback({ beep: this.opts.beep, vibrate: this.opts.vibrate });
    this.ownsVideo = !options.video;
    this.video = options.video ?? (typeof document !== "undefined" ? document.createElement("video") : (null as unknown as HTMLVideoElement));
    this.video?.addEventListener?.("resize", this.handleVideoResize);
    if (options.worker === false) this.preferWorker = false;
  }

  private preferWorker = true;

  get state(): ScannerState {
    return this._state;
  }

  get options(): Readonly<ResolvedOptions> {
    return this.opts;
  }

  private setState(s: ScannerState): void {
    if (this._state === s) return;
    this._state = s;
    this.emit("state", s);
  }

  /** Update options while running (symbologies, mode, duplicate filter, scan area…). */
  setOptions(partial: Partial<BarcodeScannerOptions>): void {
    const { video: _video, ...rest } = partial;
    const raw = { ...this.raw, ...rest };
    const merged = resolveOptions(raw);
    this.raw = raw;
    const token = ++this.frameToken;
    const modeChanged = merged.mode !== this.opts.mode;
    const enabled = new Set(merged.symbologies);
    const symbologiesChanged = enabled.size !== this.opts.symbologies.length || this.opts.symbologies.some((s) => !enabled.has(s));
    this.opts = merged;
    this.resetCapturePolicy();
    this.filter.window = merged.duplicateFilter;
    this.feedback.options = { ...this.feedback.options, beep: merged.beep, vibrate: merged.vibrate };
    if (modeChanged) {
      this.tracker.clear();
      this.pendingBatchScans.clear();
      this.filter.reset();
    } else if (symbologiesChanged) {
      const update = this.tracker.retainSymbologies(enabled);
      for (const track of update.removed) this.pendingBatchScans.delete(track.id);
      if (update.removed.length) this.emit("track", update);
    }
    if (token !== this.frameToken) return;
    if (partial.torch !== undefined && this._state === "scanning") void this.camera.setTorch(!!partial.torch);
  }

  /** Open the camera and start decoding. Call from a user gesture for sound on iOS. */
  async start(): Promise<void> {
    if (this.destroying || this._state === "scanning" || this._state === "starting") return;
    this.video?.addEventListener?.("resize", this.handleVideoResize);
    const token = ++this.startToken;
    this.frameToken++;
    this.resetCapturePolicy();
    this.setState("starting");
    if (token !== this.startToken) return;
    this.feedback.unlock();
    try {
      const [decoder] = await Promise.all([this.decoder ? Promise.resolve(this.decoder) : createFrameDecoder(this.preferWorker), this.camera.start(this.video)]);
      if (token !== this.startToken) return; // stopped while starting
      this.decoder = decoder;
      this.stats.engine = decoder.kind;
      this.filter.reset();
      this.tracker.clear();
      this.pendingBatchScans.clear();
      this.frameSize = this.currentFrameSize();
      this.setState("scanning");
      if (token !== this.startToken) return;
      if (this.raw.torch) await this.camera.setTorch(true);
      if (token !== this.startToken) return;
      this.emit("ready", { engine: decoder.kind, camera: this.camera });
      this.schedule();
    } catch (err) {
      if (token !== this.startToken) return;
      this.camera.stop();
      const e = toQRGenError(err);
      this.setState("error");
      if (token === this.startToken) this.emit("error", e);
      throw e;
    }
  }

  /** Stop decoding and release the camera. */
  stop(): void {
    this.startToken++;
    this.frameToken++;
    this.resetCapturePolicy();
    this.cancelFrame();
    this.camera.stop();
    // The shared decoder factory reuses healthy workers and replaces failed ones.
    this.decoder = null;
    this.tracker.clear();
    this.pendingBatchScans.clear();
    this.frameSize = null;
    if (this._state !== "idle") this.setState("stopped");
  }

  /** Stop decoding but keep the camera running. */
  pause(): void {
    if (this._state !== "scanning") return;
    this.frameToken++;
    this.resetCapturePolicy();
    this.cancelFrame();
    this.setState("paused");
  }

  /** Resume decoding after pause() or a single-mode scan. */
  resume(): void {
    if (this._state !== "paused") return;
    this.frameToken++;
    this.resetCapturePolicy();
    this.filter.reset();
    this.setState("scanning");
    this.schedule();
  }

  async setTorch(on: boolean): Promise<boolean> {
    return this.camera.setTorch(on);
  }

  async toggleTorch(): Promise<boolean> {
    const next = !this.camera.isTorchOn;
    return (await this.camera.setTorch(next)) ? next : this.camera.isTorchOn;
  }

  async setZoom(zoom: number): Promise<boolean> {
    return this.camera.setZoom(zoom);
  }

  async switchCamera(): Promise<void> {
    if (this.destroying) return;
    const token = this.startToken;
    // Rapid control presses share the camera operation and its failure outcome.
    if (this.pendingCameraSwitch?.token === token) return this.pendingCameraSwitch.promise;
    const operation = ++this.switchToken;
    this.frameToken++;
    this.resetCapturePolicy();
    this.cancelFrame();
    const switching = this.performCameraSwitch(token, operation);
    this.pendingCameraSwitch = { token, promise: switching };
    try {
      await switching;
    } finally {
      if (this.pendingCameraSwitch?.promise === switching) this.pendingCameraSwitch = null;
      if (token === this.startToken && operation === this.switchToken) this.schedule();
    }
  }

  private async performCameraSwitch(token: number, operation: number): Promise<void> {
    try {
      await this.camera.switch();
      if (token !== this.startToken || operation !== this.switchToken) return;
      this.tracker.clear();
      this.pendingBatchScans.clear();
      this.frameSize = this.currentFrameSize();
    } catch (err) {
      if (token !== this.startToken || operation !== this.switchToken) return;
      const e = toQRGenError(err);
      const stoppedToken = this.startToken + 1;
      this.stop();
      // A stopped listener may have opened a replacement on this same scanner.
      if (this.startToken !== stoppedToken || operation !== this.switchToken) throw e;
      this.setState("error");
      if (this.startToken === stoppedToken && operation === this.switchToken) this.emit("error", e);
      throw e;
    }
  }

  /** Release everything, including the decoder reference. */
  destroy(): void {
    if (this.destroying) return;
    this.destroying = true;
    try {
      // stop emits state synchronously: callbacks must not reopen a source while
      // this teardown is still releasing it. Explicit later reuse stays allowed.
      this.stop();
      this.feedback.dispose();
      this.removeAllListeners();
      this.video?.removeEventListener?.("resize", this.handleVideoResize);
      if (this.ownsVideo) this.video?.remove();
      this.decoder = null;
      this.setState("idle");
    } finally {
      this.destroying = false;
    }
  }

  private cancelFrame(): void {
    if (!this.frameHandle) return;
    const v = this.video as HTMLVideoElement & { cancelVideoFrameCallback?: (h: number) => void };
    if (typeof v.cancelVideoFrameCallback === "function") v.cancelVideoFrameCallback(this.frameHandle);
    cancelAnimationFrame(this.frameHandle);
    this.frameHandle = 0;
  }

  private schedule(): void {
    if (this._state !== "scanning" || this.frameHandle || this.pendingCameraSwitch?.token === this.startToken) return;
    const v = this.video as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number };
    const tick = () => {
      this.frameHandle = 0;
      void this.onFrame();
    };
    this.frameHandle = typeof v.requestVideoFrameCallback === "function" ? v.requestVideoFrameCallback(tick) : requestAnimationFrame(tick);
  }

  private resetCapturePolicy(): void {
    this.capturePolicy = this.opts.adaptive ? new AdaptiveCapturePolicy(this.opts.adaptive) : null;
    this.stats.adaptive = null;
  }

  private currentFrameSize(): Size | null {
    const width = this.video.videoWidth;
    const height = this.video.videoHeight;
    return width && height ? { width, height } : null;
  }

  /** A CSS resize only reprojects overlays; an intrinsic resize starts a new coordinate space. */
  private syncFrameSize(): void {
    if (this._state !== "scanning" && this._state !== "paused" && this._state !== "starting") return;
    const next = this.currentFrameSize();
    if (!next) return;
    const previous = this.frameSize;
    this.frameSize = next;
    if (!previous || (previous.width === next.width && previous.height === next.height)) return;
    this.frameToken++;
    this.resetCapturePolicy();
    const dropped = this.tracker.all;
    this.tracker.clear();
    this.pendingBatchScans.clear();
    // Listeners that keep their own tracks from added/removed must drop these too.
    if (dropped.length) this.emit("track", { tracked: [], added: [], updated: [], removed: dropped });
    this.emit("spatialreset", { frameSize: next });
  }

  private isCurrentFrame(token: number, size: Size): boolean {
    if (token !== this.frameToken || this._state !== "scanning") return false;
    // Some browsers deliver the video resize event after a queued decode completes.
    this.syncFrameSize();
    return token === this.frameToken && this._state === "scanning"
      && size.width === this.video.videoWidth && size.height === this.video.videoHeight;
  }

  private grab(plan?: AdaptiveCapturePlan): { image: ImageData; offsetX: number; offsetY: number; scale: number; scaleX?: number; scaleY?: number; frame: Size } | null {
    const vw = this.video.videoWidth;
    const vh = this.video.videoHeight;
    if (!vw || !vh) return null;
    const a = this.opts.scanArea;
    const sx = a ? Math.max(0, Math.round(a.x * vw)) : 0;
    const sy = a ? Math.max(0, Math.round(a.y * vh)) : 0;
    const sw = a ? Math.min(vw - sx, Math.round(a.width * vw)) : vw;
    const sh = a ? Math.min(vh - sy, Math.round(a.height * vh)) : vh;
    if (sw < 16 || sh < 16) return null;
    if (plan && (!Number.isFinite(sw) || !Number.isFinite(sh))) return null;
    const scale = Math.max(1, Math.max(sw, sh) / (plan?.maxDecodeSize ?? this.opts.maxDecodeSize), plan ? Math.sqrt(sw * sh / plan.maxDecodePixels) : 1);
    // Floor only adaptive dimensions so neither side nor total pixels can exceed its budget.
    let dw = plan ? Math.max(1, Math.floor(sw / scale)) : Math.round(sw / scale);
    let dh = plan ? Math.max(1, Math.floor(sh / scale)) : Math.round(sh / scale);
    if (plan) {
      // A very skinny area can clamp one dimension to 1px after scaling. Enforce
      // the product again so that minimum-one rounding cannot bypass the budget.
      dw = Math.min(dw, plan.maxDecodePixels);
      dh = Math.min(dh, Math.floor(plan.maxDecodePixels / dw));
    }
    if (!this.canvas || this.canvas.width !== dw || this.canvas.height !== dh) {
      this.canvas = typeof OffscreenCanvas !== "undefined" ? new OffscreenCanvas(dw, dh) : Object.assign(document.createElement("canvas"), { width: dw, height: dh });
      this.ctx = this.canvas.getContext("2d", { willReadFrequently: true }) as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    }
    if (!this.ctx) return null;
    this.ctx.drawImage(this.video, sx, sy, sw, sh, 0, 0, dw, dh);
    return {
      image: this.ctx.getImageData(0, 0, dw, dh), offsetX: sx, offsetY: sy, scale,
      ...(plan ? { scaleX: sw / dw, scaleY: sh / dh } : {}),
      frame: { width: vw, height: vh },
    };
  }

  private async onFrame(): Promise<void> {
    if (this._state !== "scanning" || !this.decoder || this.pendingCameraSwitch?.token === this.startToken) return;
    this.syncFrameSize();
    // A reset callback may stop, pause or replace the source.
    if (this._state !== "scanning" || !this.decoder || this.pendingCameraSwitch?.token === this.startToken) return;
    const now = performance.now();
    const minInterval = 1000 / this.opts.maxFps;
    const hidden = typeof document !== "undefined" && document.visibilityState === "hidden";
    if (this.busy || hidden || now - this.lastDecodeStart < minInterval) return this.schedule();
    const policy = this.capturePolicy;
    const plan = policy?.next();
    let grabbed: ReturnType<BarcodeScanner["grab"]>;
    try {
      grabbed = this.grab(plan);
    } catch (err) {
      const stoppedToken = this.startToken + 1;
      this.stop();
      if (this.startToken === stoppedToken) this.emit("error", toQRGenError(err));
      return;
    }
    if (!grabbed) return this.schedule();
    this.busy = true;
    this.lastDecodeStart = now;
    this.frameCount++;
    const token = this.frameToken;
    const o = this.opts;
    const params: DecodeParams = {
      formats: toZXingReadFormats(o.symbologies),
      tryHarder: o.tryHarder,
      tryRotate: true,
      tryInvert: o.tryInvert === "alternate" ? this.frameCount % 2 === 0 : o.tryInvert,
      tryDownscale: true,
      maxNumberOfSymbols: o.maxResults,
    };
    // Schedule the next frame now so the video callback chain never stalls.
    this.schedule();
    try {
      const decodeStart = plan ? performance.now() : now;
      const raws = await this.decoder.decode(grabbed.image, params);
      if (!this.isCurrentFrame(token, grabbed.frame)) return;
      const finished = performance.now();
      const decodeMs = finished - now;
      this.stats.decodeMs = this.stats.decodeMs ? this.stats.decodeMs * 0.8 + decodeMs * 0.2 : decodeMs;
      this.fpsWindow.push(now);
      while (this.fpsWindow.length && now - this.fpsWindow[0]! > 1000) this.fpsWindow.shift();
      this.stats.fps = this.fpsWindow.length;
      if (this._state !== "scanning") return;
      const requested = o.symbologies.length === ALL_SYMBOLOGIES.length ? undefined : new Set(o.symbologies);
      const barcodes = toBarcodes(raws, {
        frameSize: grabbed.frame,
        offsetX: grabbed.offsetX,
        offsetY: grabbed.offsetY,
        scale: grabbed.scale,
        scaleX: grabbed.scaleX,
        scaleY: grabbed.scaleY,
        requested,
      });
      if (policy && plan) {
        policy.observe(plan, finished - decodeStart, barcodes.length > 0);
        this.stats.adaptive = {
          ...policy.snapshot,
          phase: plan.phase,
          width: grabbed.image.width,
          height: grabbed.image.height,
          decodeMs: finished - decodeStart,
        };
      }
      this.emit("frame", { barcodes, frameSize: grabbed.frame, decodeMs });
      if (!this.isCurrentFrame(token, grabbed.frame)) return;
      this.handleResults(barcodes);
    } catch (err) {
      if (!this.isCurrentFrame(token, grabbed.frame)) return;
      const e = toQRGenError(err);
      if (e.code === "engine-load-failed") {
        const stoppedToken = this.startToken + 1;
        this.stop();
        if (this.startToken === stoppedToken) this.emit("error", e);
      }
    } finally {
      this.busy = false;
    }
  }

  private handleResults(barcodes: Barcode[]): void {
    const o = this.opts;
    if (o.mode === "batch") {
      const token = this.frameToken;
      const update = this.tracker.update(barcodes);
      for (const b of update.removed) this.pendingBatchScans.delete(b.id);
      for (const b of update.added) this.pendingBatchScans.add(b.id);
      this.emit("track", update);
      this.syncFrameSize();
      if (token !== this.frameToken || this._state !== "scanning") return;
      // An earlier track callback may cancel scan delivery without removing the track.
      // Retry its first report only when the barcode is observed in this frame.
      const candidates = [...update.added, ...update.updated].filter((b) => this.pendingBatchScans.has(b.id));
      const fresh = candidates.filter((b) => this.filter.accept(b));
      for (const b of candidates) this.pendingBatchScans.delete(b.id);
      if (fresh.length) {
        const t = Date.now();
        if (t - this.lastBatchBeep > 250) {
          this.feedback.success();
          this.lastBatchBeep = t;
        }
        this.emit("scan", { barcodes: fresh });
      }
      return;
    }
    const fresh = barcodes.filter((b) => this.filter.accept(b));
    if (!fresh.length) return;
    this.feedback.success();
    if (o.mode === "single") {
      // pause() invalidates this frame once; listener changes invalidate it again.
      const pausedToken = this.frameToken + 1;
      this.pause();
      if (this.frameToken !== pausedToken || this._state !== "paused") return;
    }
    this.emit("scan", { barcodes: fresh });
  }
}
